import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Papa from 'papaparse';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { providerTenantsService } from '../../services/ProviderTenantsService';
import { opsDevicesService } from '../../services/OpsDevicesService';
import type { OpsDeviceDetail, OpsIotProvisioningStatus, OpsDeviceCategory, DeviceInventoryStatus } from '@cnapse/shared/schemas.js';
import { sanitizePemCertificateInput } from '@cnapse/shared/pem-utils.js';
import type { Tenant } from '../../services/apiTypes';
import DecommissionModal from './components/DecommissionModal';
import ResetDeviceModal from './components/ResetDeviceModal';
import DeviceFormDialog from './components/DeviceFormDialog';
import { EnhancedTable, type TableColumn, type TableAction, type FilterConfig } from '../../components/ui/EnhancedTable';
import { AppButton, AppIconButton, AppListPage } from '../../components/design-system';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import {
  getDeviceCategoryLabel,
  getDeviceCategorySelectOptions,
  isNonIotDeviceCategory,
} from '@cnapse/shared';

const OPS_IOT_PROVISIONING_STATUS_COLORS: Record<OpsIotProvisioningStatus, 'default' | 'info' | 'success' | 'error'> = {  
  provisioned: 'success',
  not_provisioned: 'error',
};

const OPS_IOT_PROVISIONING_STATUS_LABELS: Record<OpsIotProvisioningStatus, string> = {  
  provisioned: 'Provisioned',
  not_provisioned: 'Not Provisioned',
};

function isIotDevice(category: OpsDeviceDetail['deviceCategory']): boolean {
  return !isNonIotDeviceCategory(category);
}

const NON_IOT_STATUS_COLORS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  'in-stock': 'default',
  allocated: 'warning',
  shipped: 'info',
  claimed: 'info',
  active: 'success',
  returned: 'warning',
  retired: 'error',
};

const NON_IOT_STATUS_LABELS: Record<string, string> = {
  'in-stock': 'In Stock',
  allocated: 'Allocated',
  shipped: 'Shipped',
  claimed: 'Claimed',
  active: 'Active',
  returned: 'Returned',
  retired: 'Retired',
};

const INVENTORY_STATUS_COLORS = NON_IOT_STATUS_COLORS;
const INVENTORY_STATUS_LABELS = NON_IOT_STATUS_LABELS;

const IOT_REGISTER_CATEGORIES = new Set(['efem-gateway', 'location-gateway']);

export default function DeviceInventory() {
  const [devices, setDevices] = useState<OpsDeviceDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const [filters, setFilters] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  // Modals
  const [editTarget, setEditTarget] = useState<OpsDeviceDetail | null>(null);
  const [decommissionTarget, setDecommissionTarget] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Register/Edit device dialog
  const [deviceFormOpen, setDeviceFormOpen] = useState(false);

  // CSV import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importType, setImportType] = useState<'non-iot' | 'iot'>('non-iot');
  const [importFileName, setImportFileName] = useState('');
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importSummary, setImportSummary] = useState<{ succeeded: number; failed: number; errors: string[]; warnings: string[] } | null>(null);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    providerTenantsService
      .getTenants()
      .then((list) => setTenants(list))
      .catch(() => { });
  }, []);

  const buildQuery = useCallback(
    (cursor?: string): {
      inventoryStatus?: DeviceInventoryStatus;
      tenantId?: string;
      deviceCategory?: OpsDeviceCategory;
      search?: string;
      cursor?: string;
      limit: number;
    } => ({
      inventoryStatus: (filters.status as DeviceInventoryStatus) || undefined,
      tenantId: filters.tenant || undefined,
      deviceCategory: (filters.category as OpsDeviceCategory) || undefined,
      search: searchTerm || undefined,
      cursor,
      limit: 50,
    }),
    [filters, searchTerm]
  );

  const filterOptions = useMemo((): FilterConfig[] => [
    {
      field: 'category',
      label: 'Category',
      type: 'select',
      options: getDeviceCategorySelectOptions([
        'efem-gateway',
        'location-tag',
        'location-anchor',
        'barcode-scanner',
        'handheld-barcode-scanner',
        'waferid-camera',
      ]),
    },
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'In Stock', value: 'in-stock' },
        { label: 'Allocated', value: 'allocated' },
        { label: 'Shipped', value: 'shipped' },
        { label: 'Claimed', value: 'claimed' },
        { label: 'Active', value: 'active' },
        { label: 'Returned', value: 'returned' },
        { label: 'Retired', value: 'retired' },
      ],
    },
    {
      field: 'tenant',
      label: 'Tenant',
      type: 'select',
      options: tenants.map((t) => ({ label: t.name, value: t.id })),
    },
  ], [tenants]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPage(1);
    try {
      const res = await opsDevicesService.listDevices(buildQuery());
      setDevices(res.data.devices);
      setNextCursor(res.data.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await opsDevicesService.listDevices(buildQuery(nextCursor));
      setDevices((prev) => [...prev, ...res.data.devices]);
      setNextCursor(res.data.nextCursor ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDecommissionSuccess = () => {
    setSuccessMsg(`Device decommissioned.`);
    load();
  };

  const handleResetSuccess = (sn: string) => {
    setSuccessMsg(`Device ${sn} reset to manufactured.`);
    load();
  };

  const columns: TableColumn<OpsDeviceDetail>[] = [
    {
      field: 'serialNumber',
      headerName: 'Cnapse Serial',
      minWidth: 160,
      renderCell: (d: OpsDeviceDetail) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          component="a"
          href={`/provider/devices/${d.serialNumber}`}
          onClick={(e) => {
            e.preventDefault();
            window.history.pushState({}, '', `/provider/devices/${d.serialNumber}`);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
          sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'none' }}
        >
          {d.serialNumber}
        </Typography>
      ),
    },
    {
      field: 'vendorSerialNumber',
      headerName: 'Vendor Serial',
      minWidth: 170,
      renderCell: (d: OpsDeviceDetail) => (
        <Typography variant="body2" fontFamily="monospace">
          {d.vendorSerialNumber ?? 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'deviceModel',
      headerName: 'Model',
      minWidth: 100,
      renderCell: (d: OpsDeviceDetail) => d.deviceModel ?? 'N/A',
    },
    {
      field: 'deviceCategory',
      headerName: 'Category',
      minWidth: 140,
      renderCell: (d: OpsDeviceDetail) => (
          <Chip
            label={getDeviceCategoryLabel(d.deviceCategory)}
            size="small"
            variant="outlined"
            color="default"
          />
        ),
    },
    {
      field: 'inventoryStatus',
      headerName: 'Status',
      minWidth: 130,
      renderCell: (d: OpsDeviceDetail) => {
        const inventoryStatus = d.inventoryStatus ?? 'in-stock';
        return (
          <Chip
            label={INVENTORY_STATUS_LABELS[inventoryStatus] ?? inventoryStatus}
            color={INVENTORY_STATUS_COLORS[inventoryStatus] ?? 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'iotProvisioningStatus',
      headerName: 'Provisioning',
      minWidth: 140,
      renderCell: (d: OpsDeviceDetail) => {
        if (!isIotDevice(d.deviceCategory)) {
          return (
            <Typography variant="body2" color="text.secondary">
              N/A
            </Typography>
          );
        }
        const iotProvisioningStatus: OpsIotProvisioningStatus = d.iotProvisioningStatus ?? 'not_provisioned';
        return (
          <Chip
            label={OPS_IOT_PROVISIONING_STATUS_LABELS[iotProvisioningStatus]}
            color={OPS_IOT_PROVISIONING_STATUS_COLORS[iotProvisioningStatus]}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'resolvedTenantName',
      headerName: 'Tenant',
      minWidth: 160,
      renderCell: (d: OpsDeviceDetail) => (
        <Typography variant="body2">{d.resolvedTenantName ?? 'N/A'}</Typography>
      ),
    },
    {
      field: 'allocatedAt',
      headerName: 'Assigned',
      minWidth: 120,
      renderCell: (d: OpsDeviceDetail) => (
        <Typography variant="body2">
          {d.allocatedAt ? new Date(d.allocatedAt).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
  ];

  const rowActions: TableAction<OpsDeviceDetail>[] = [
    {
      label: 'Edit device',
      icon: <EditIcon fontSize="small" />,
      visible: () => true,
      onClick: (d) => setEditTarget(d),
      color: 'primary',
    }
  ];

  const pagedDevices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return devices.slice(start, start + pageSize);
  }, [devices, page, pageSize]);

  const handleFilterChange = useCallback((newFilters: import('../../components/ui/EnhancedTable/types').TableFilters) => {
    setFilters(newFilters as Record<string, string>);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const resetImportState = (type: 'non-iot' | 'iot') => {
    setImportType(type);
    setImportFileName('');
    setImportRows([]);
    setImportError(null);
    setImportSummary(null);
  };

  const downloadImportTemplate = (type: 'non-iot' | 'iot') => {
    const csv = type === 'iot'
      ? [
          'serialNumber,vendorSerialNumber,ekCertPem,akCertPem,manufacturerName,model,mfgBatch,mfgDate(MM/DD/YYYY),deviceCategory,macAddress,notes',
          ',GW-1001,"-----BEGIN CERTIFICATE-----\\n...\\n-----END CERTIFICATE-----","-----BEGIN CERTIFICATE-----\\n...\\n-----END CERTIFICATE-----",Acme Devices,LS-GW-1,BATCH-2026-Q2,05/01/2026,location-gateway,AA:BB:CC:DD:EE:01,Initial onboarding',
        ].join('\n')
      : [
          'serialNumber,vendorSerialNumber,macAddress,deviceCategory,manufacturerName,model,mfgBatch,mfgDate(MM/DD/YYYY),vendorDeviceId,notes',
          ',ANCH-1001,AA:BB:CC:DD:EE:11,location-anchor,Acme Devices,LS-1000,BATCH-2026-Q2,05/01/2026,BD125,Initial onboarding',
        ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = type === 'iot' ? 'iot-device-import-template.csv' : 'non-iot-device-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportFilePicked = (file: File | null) => {
    if (!file) return;

    setImportError(null);
    setImportSummary(null);
    setImportFileName(file.name);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = (results.data ?? []).filter((row) =>
          Object.values(row).some((v) => (v ?? '').toString().trim().length > 0)
        );
        if (rows.length === 0) {
          setImportRows([]);
          setImportError('CSV has no data rows.');
          return;
        }
        setImportRows(rows);
      },
      error: () => {
        setImportRows([]);
        setImportError('Failed to parse CSV file.');
      },
    });
  };

  const processCsvImport = async () => {
    if (importRows.length === 0) {
      setImportError('Please upload a CSV file with at least one row.');
      return;
    }

    const errors: string[] = [];
    const warnings: string[] = [];
    let succeeded = 0;

    setImportLoading(true);
    setImportError(null);
    setImportSummary(null);

    for (let i = 0; i < importRows.length; i += 1) {
      const row = importRows[i];
      const rowNum = i + 2;

      try {
        if (importType === 'iot') {
          const serialNumber = (row.serialNumber ?? '').trim();
          const vendorSerialNumber = (row.vendorSerialNumber ?? '').trim();
          const ekCertPem = sanitizePemCertificateInput(row.ekCertPem ?? '');
          const akCertPem = sanitizePemCertificateInput(row.akCertPem ?? '');
          const manufacturerName = (row.manufacturerName ?? '').trim();
          const model = (row.model ?? '').trim();
          const mfgBatch = (row.mfgBatch ?? '').trim();
          const mfgDate = (row.mfgDate ?? '').trim();
          const rawCategory = (row.deviceCategory ?? 'efem-gateway').trim();
          const macAddress = (row.macAddress ?? '').trim();
          const notes = (row.notes ?? '').trim();

          if (!vendorSerialNumber || !ekCertPem || !akCertPem || !model || !mfgBatch || !mfgDate) {
            throw new Error('Missing required fields: vendorSerialNumber, ekCertPem, akCertPem, model, mfgBatch, mfgDate');
          }
          if (!IOT_REGISTER_CATEGORIES.has(rawCategory)) {
            throw new Error('deviceCategory must be efem-gateway or location-gateway');
          }

          await opsDevicesService.registerIotDevice({
            serialNumber: serialNumber || undefined,
            vendorSerialNumber,
            ekCertPem,
            akCertPem,
            manufacturerName: manufacturerName || undefined,
            model,
            mfgBatch,
            mfgDate,
            deviceCategory: rawCategory as 'efem-gateway' | 'location-gateway',
            macAddress: macAddress || undefined,
            notes: notes || undefined,
          });
        } else {
          const serialNumber = (row.serialNumber ?? '').trim();
          const vendorSerialNumber = (row.vendorSerialNumber ?? '').trim();
          const macAddress = (row.macAddress ?? '').trim();
          const deviceCategory = (row.deviceCategory ?? '').trim();
          const manufacturerName = (row.manufacturerName ?? '').trim();
          const model = (row.model ?? '').trim();
          const mfgBatch = (row.mfgBatch ?? '').trim();
          const mfgDate = (row.mfgDate ?? '').trim();
          const vendorDeviceId = (row.vendorDeviceId ?? '').trim();
          const notes = (row.notes ?? '').trim();

          if (!vendorSerialNumber || !macAddress || !deviceCategory || !model || !mfgBatch || !mfgDate) {
            throw new Error('Missing required fields: vendorSerialNumber, macAddress, deviceCategory, model, mfgBatch, mfgDate');
          }

          await opsDevicesService.registerNonIotDevice({
            serialNumber: serialNumber || undefined,
            vendorSerialNumber,
            macAddress,
            deviceCategory,
            manufacturerName: manufacturerName || undefined,
            model,
            mfgBatch,
            mfgDate,
            vendorDeviceId: vendorDeviceId || undefined,
            notes: notes || undefined,
          });
        }

        succeeded += 1;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        errors.push(`Row ${rowNum}: ${msg}`);
      }
    }

    setImportLoading(false);
    setImportSummary({ succeeded, failed: errors.length, errors, warnings });
    if (errors.length === 0) {
      setSuccessMsg(`CSV import completed: ${succeeded} row(s) registered.`);
      setImportOpen(false);
      resetImportState(importType);
    } else {
      setImportError(`Completed with ${errors.length} error(s).`);
    }

    await load();
  };

  const toolbarPageActions = useMemo(
    () => (
      <AppIconButton onClick={() => void load()} disabled={loading} aria-label="Refresh">
        <RefreshIcon />
      </AppIconButton>
    ),
    [load, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppListPage>
      {error && (
        <Alert severity="error" sx={{ flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <EnhancedTable<OpsDeviceDetail>
        columns={columns}
        actions={rowActions}
        data={pagedDevices}
        totalCount={devices.length}
        loading={loading}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size: number) => {
          setPageSize(size);
          setPage(1);
        }}
        getRowId={(d) => d.serialNumber}
        searchable
        searchTerm={searchTerm}
        searchPlaceholder="Search by serial, batch, tenant"
        onSearchChange={handleSearchChange}
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        emptyStateTitle="No devices found"
        emptyStateDescription="Adjust filters or check back later."
        onAdd={() => {
          setEditTarget(null);
          setDeviceFormOpen(true);
        }}
        addLabel="Register device"
        toolbarLeftContent={
          nextCursor ? (
            <Typography variant="body2" color="text.secondary">
              {devices.length} loaded - more available
            </Typography>
          ) : undefined
        }
        toolbarActions={[
          {
            label: 'Import CSV',
            icon: <UploadFileIcon />,
            onClick: () => {
              resetImportState('non-iot');
              setImportOpen(true);
            },
            variant: 'outlined',
          },
          ...(nextCursor
            ? [
                {
                  label: loadingMore ? 'Loading...' : 'Load more',
                  onClick: () => void loadMore(),
                  variant: 'outlined' as const,
                  disabled: loadingMore,
                },
              ]
            : []),
        ]}
        rowHeight={56}
        stickyHeader
        maxHeight="100%"
      />

      {/* Snackbar */}
      {successMsg && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, minWidth: 300 }}
          onClose={() => setSuccessMsg('')}
        >
          {successMsg}
        </Alert>
      )}

      {/* Modals */}
      <DecommissionModal
        open={!!decommissionTarget}
        serialNumber={decommissionTarget ?? ''}
        onClose={() => setDecommissionTarget(null)}
        onSuccess={handleDecommissionSuccess}
      />

      <ResetDeviceModal
        open={!!resetTarget}
        serialNumber={resetTarget ?? ''}
        onClose={() => setResetTarget(null)}
        onSuccess={handleResetSuccess}
      />
      <DeviceFormDialog
        open={deviceFormOpen || !!editTarget}
        mode={editTarget ? 'edit' : 'add'}
        device={editTarget}
        onClose={() => { setDeviceFormOpen(false); setEditTarget(null); }}
        onSuccess={(msg) => { setSuccessMsg(msg); setDeviceFormOpen(false); setEditTarget(null); void load(); }}
      />

      {/* CSV Import Dialog */}
      <Dialog open={importOpen} onClose={() => !importLoading && setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Devices from CSV</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {importError && <Alert severity="error">{importError}</Alert>}
            {importSummary && (
              <Alert severity={importSummary.failed > 0 ? 'warning' : importSummary.warnings.length > 0 ? 'warning' : 'success'}>
                Imported {importSummary.succeeded} row(s); {importSummary.failed} failed.
                {importSummary.warnings.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto', fontSize: '0.8rem' }}>
                    <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                      {importSummary.warnings.length} warning(s) - Peripheral Device Registry:
                    </Typography>
                    {importSummary.warnings.map((w) => (
                      <Typography key={w} variant="caption" display="block">{w}</Typography>
                    ))}
                  </Box>
                )}
                {importSummary.errors.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 120, overflowY: 'auto', fontSize: '0.8rem' }}>
                    {importSummary.errors.slice(0, 10).map((e) => (
                      <Typography key={e} variant="caption" display="block">{e}</Typography>
                    ))}
                    {importSummary.errors.length > 10 && (
                      <Typography variant="caption" display="block">+{importSummary.errors.length - 10} more errors</Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Import Type</Typography>
              <ToggleButtonGroup
                value={importType}
                exclusive
                onChange={(_e, val) => {
                  if (!val) return;
                  resetImportState(val as 'non-iot' | 'iot');
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="non-iot" sx={{ flex: 1 }}>Non-IoT Device CSV</ToggleButton>
                <ToggleButton value="iot" sx={{ flex: 1 }}>IoT Gateway CSV</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <AppButton
              variant="text"
              startIcon={<DownloadIcon />}
              onClick={() => downloadImportTemplate(importType)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download Template
            </AppButton>

            <input
              ref={importFileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              aria-label="Import devices CSV file"
              onChange={(e) => handleImportFilePicked(e.target.files?.[0] ?? null)}
            />
            <AppButton
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => importFileInputRef.current?.click()}
            >
              {importFileName ? `Selected: ${importFileName}` : 'Choose CSV File'}
            </AppButton>

            {importRows.length > 0 && (
              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                Parsed {importRows.length} row(s). Click "Import" to register devices.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={() => setImportOpen(false)} disabled={importLoading}>Cancel</AppButton>
          <AppButton
            variant="contained"
            disabled={importLoading || importRows.length === 0}
            onClick={processCsvImport}
            startIcon={importLoading ? <CircularProgress size={16} /> : undefined}
          >
            Import
          </AppButton>
        </DialogActions>
      </Dialog>
    </AppListPage>
  );
}
